import { Injectable } from '@angular/core';
import { Customer, Product } from '../../../modals/product.modals';
import { ClientSettingsService } from '../../../services/client-settings.service';
import { IndustryService } from '../../../services/industry.service';
import { DynamicTemplateConfigService } from '../../../services/pdf-generation/dynamic-template-config.service';

/**
 * One cart line billed at its incl-tax (shelf) price, with GST worked back from the
 * line value: 300.48 x 3 = 901.44 -> taxable 804.86, CGST 48.29, SGST 48.29.
 * Built by DashboardComponent.getInclLineBreakup; null means the line uses excl price + tax.
 */
export interface InclLineBreakup {
  gross: number;
  taxable: number;
  cgst: number;
  sgst: number;
  igst: number;
}

/**
 * PosCalculationService
 *
 * Dedicated service for POS financial calculations, item counters,
 * tax computations (CGST/SGST/IGST/CESS), overall discounts, free item amounts,
 * and quotation/variant validations.
 *
 * Extracted from DashboardComponent to decouple calculation logic while
 * preserving 100% identical business rules.
 */
@Injectable({
  providedIn: 'root',
})
export class PosCalculationService {
  constructor(
    private industryService: IndustryService,
    private dynamicTemplateConfigService: DynamicTemplateConfigService,
    private clientSettingsService: ClientSettingsService,
  ) {}

  /**
   * Get unique items count in cart
   */
  getUniqueItemsCount(products: Product[], isExchangeOrder: boolean): number {
    let uniqueCount = 0;
    for (const product of products || []) {
      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          uniqueCount++;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          uniqueCount++;
        }
      }
    }
    return uniqueCount;
  }

  /**
   * Get total quantity count of products in cart
   */
  getTotalCount(products: Product[], isExchangeOrder: boolean): number {
    let totalCount = 0;
    for (const product of products || []) {
      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          totalCount += exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          totalCount += product.increaseQuantity;
        }
      }
    }
    return parseFloat(totalCount.toFixed(2));
  }

  /**
   * Get line-item discount total
   */
  getDisctotal(products: Product[], isExchangeOrder: boolean): number {
    let discamt = 0;
    for (const product of products || []) {
      if (this.industryService.isMobile() && product.isFree) continue;

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          const discountValue = Number(product.discountval) || 0;
          discamt += discountValue * exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          const discountValue = Number(product.discountval) || 0;
          discamt += discountValue * product.increaseQuantity;
        }
      }
    }
    return discamt;
  }

  /**
   * Get total amount of free items (Mobile vertical only)
   */
  getFreeAmount(
    products: Product[],
    getEffectivePriceFn: (product: Product) => number,
  ): number {
    if (!this.industryService.isMobile()) return 0;
    return (products || []).reduce((sum, product) => {
      if (product.isFree && product.increaseQuantity && !product.returned) {
        const price = Number(getEffectivePriceFn(product));
        return sum + price * product.increaseQuantity;
      }
      return sum;
    }, 0);
  }

  /**
   * Calculate total Profit Amount (Margin Amount) for all active cart items.
   *
   * Architecture note: This lives here (PosCalculationService) because:
   *   1. It follows the exact same SRP pattern as getSubtotal / getDisctotal.
   *   2. It uses the same `products` array passed to all other calculations, so
   *      it automatically re-derives whenever the cart changes — no manual
   *      cache-busting or event plumbing required.
   *   3. DashboardComponent calls it via getters and passes the result as an
   *      @Input() to DashboardRightPanelComponent, keeping the data-flow
   *      identical to every other total displayed in the right panel.
   *
   * Formula: Σ (product.marginamount × activeQuantity)
   *   – Excludes "free" items (isFree) and exchange-return rows (returned).
   *   – Uses exchangeQuantity when in exchange-order mode.
   */
  getProfitAmount(
    products: Product[],
    isExchangeOrder: boolean,
    productDiscount: number = 0,
    overAllDiscountAmount: number = 0,
  ): number {
    let grossProfit = 0;
    for (const product of products || []) {
      // Skip free items (no margin applies to giveaway stock)
      if (product.isFree) continue;

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          grossProfit += (Number(product.marginamount) || 0) * exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          grossProfit += (Number(product.marginamount) || 0) * product.increaseQuantity;
        }
      }
    }

    const netProfit = grossProfit - (productDiscount || 0) - (overAllDiscountAmount || 0);
    return Math.max(0, Math.round(netProfit * 100) / 100);
  }

  /**
   * Get cart subtotal
   */
  getSubtotal(
    products: Product[],
    isExchangeOrder: boolean,
    getEffectivePriceFn: (product: Product) => number,
    getInclLineFn?: (product: Product) => InclLineBreakup | null,
  ): number {
    let subtotal: number = 0;
    for (const product of products || []) {
      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          const priceAfterTax = Number(getEffectivePriceFn(product));
          subtotal += priceAfterTax * exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          const incl = getInclLineFn?.(product);
          const priceAfterTax = Number(getEffectivePriceFn(product));
          subtotal += incl
            ? incl.taxable
            : priceAfterTax * product.increaseQuantity;
        }
      }
    }
    const freeAmount = this.getFreeAmount(products, getEffectivePriceFn);
    return Math.max(0, subtotal - freeAmount);
  }

  /**
   * Determine whether interstate tax (IGST) applies based on customer state vs store state
   */
  isInterStateTax(selectedCustomer: Customer | any): boolean {
    const clientData = this.dynamicTemplateConfigService.getClientData();
    const storeStateName = clientData?.state_name || '';
    //  console.log(selectedCustomer,storeStateName , '<no store state>','customer state-----')
    if (!storeStateName || !selectedCustomer) {
      return false;
    }

    const customerState = selectedCustomer.state || '';
   
    if (!customerState) {
      return false;
    }

    const isSameState =
      storeStateName.trim().toLowerCase() ===
      customerState.trim().toLowerCase();

    return !isSameState;
  }

  /**
   * Calculate CGST amount
   */
  getCGST(
    products: Product[],
    isExchangeOrder: boolean,
    isSuspenseMode: boolean,
    selectedCustomer: Customer | null,
    getEffectivePriceFn: (product: Product) => number,
    getInclLineFn?: (product: Product) => InclLineBreakup | null,
  ): number {
    if (this.isInterStateTax(selectedCustomer)) {
      return 0;
    }

    let cgst = 0;
    for (const product of products || []) {
      if (isSuspenseMode && product.isgst === 0) {
        continue;
      }
      if (this.industryService.isMobile() && product.isFree) continue;

      const effectivePrice = getEffectivePriceFn(product);

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          cgst +=
            ((Number(effectivePrice) * Number(product.ctax)) / 100) *
            exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          const incl = getInclLineFn?.(product);
          cgst += incl
            ? incl.cgst
            : ((Number(effectivePrice) * Number(product.ctax)) / 100) *
              product.increaseQuantity;
        }
      }
    }

    return cgst;
  }

  /**
   * Calculate SGST amount
   */
  getSGST(
    products: Product[],
    isExchangeOrder: boolean,
    isSuspenseMode: boolean,
    selectedCustomer: Customer | null,
    getEffectivePriceFn: (product: Product) => number,
    getInclLineFn?: (product: Product) => InclLineBreakup | null,
  ): number {
    if (this.isInterStateTax(selectedCustomer)) {
      return 0;
    }

    let sgst = 0;
    for (const product of products || []) {
      if (isSuspenseMode && product.isgst === 0) {
        continue;
      }
      if (this.industryService.isMobile() && product.isFree) continue;

      const effectivePrice = getEffectivePriceFn(product);

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          sgst +=
            ((Number(effectivePrice) * Number(product.stax)) / 100) *
            exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          const incl = getInclLineFn?.(product);
          sgst += incl
            ? incl.sgst
            : ((Number(effectivePrice) * Number(product.stax)) / 100) *
              product.increaseQuantity;
        }
      }
    }
    return sgst;
  }

  /**
   * Calculate IGST amount
   */
  getIGST(
    products: Product[],
    isExchangeOrder: boolean,
    isSuspenseMode: boolean,
    selectedCustomer: Customer | null,
    getEffectivePriceFn: (product: Product) => number,
    getInclLineFn?: (product: Product) => InclLineBreakup | null,
  ): number {
    if (!this.isInterStateTax(selectedCustomer)) {
      return 0;
    }

    let igst = 0;
    for (const product of products || []) {
      if (isSuspenseMode && product.isgst === 0) {
        continue;
      }
      if (this.industryService.isMobile() && product.isFree) continue;

      const effectivePrice = getEffectivePriceFn(product);
      const taxRate = Number(product.itax || 0);

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          igst += ((Number(effectivePrice) * taxRate) / 100) * exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          const incl = getInclLineFn?.(product);
          igst += incl
            ? incl.igst
            : ((Number(effectivePrice) * taxRate) / 100) *
              product.increaseQuantity;
        }
      }
    }
    return igst;
  }

  /**
   * Calculate CESS amount
   */
  getCESS(
    products: Product[],
    isExchangeOrder: boolean,
    isSuspenseMode: boolean,
    getEffectivePriceFn: (product: Product) => number,
  ): number {
    let cess = 0;
    for (const product of products || []) {
      if (isSuspenseMode && product.isgst === 0) {
        continue;
      }
      if (this.industryService.isMobile() && product.isFree) continue;

      const effectivePrice = getEffectivePriceFn(product);

      if (isExchangeOrder) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          cess +=
            ((Number(effectivePrice) * Number(product.cess)) / 100) *
            exchangeQty;
        }
      } else {
        const shouldInclude = product?.increaseQuantity && !product?.returned;
        if (shouldInclude) {
          cess +=
            ((Number(effectivePrice) * Number(product.cess)) / 100) *
            product.increaseQuantity;
        }
      }
    }
    return cess;
  }

  /**
   * Calculate Total before discount adjustments
   */
  getTotal(
    subtotal: number,
    igst: number,
    sgst: number,
    cgst: number,
    cess: number,
  ): number {
    return (
      Number(subtotal) +
      Number(igst) +
      Number(sgst) +
      Number(cgst) +
      Number(cess)
    );
  }

  /**
   * Calculate Exact Total (including discounts & exchange prorated shares)
   */
  getExactTotal(
    products: Product[],
    isExchangeOrder: boolean,
    isDiscountAfterTaxEnabled: boolean,
    total: number,
    overAllDiscountAmount: number,
  ): number {
    let baseTotal = total;

    if (!isExchangeOrder && isDiscountAfterTaxEnabled) {
      let discamt = 0;
      (products || []).forEach((product) => {
        discamt +=
          Number(product.discountval) * Number(product.increaseQuantity) || 0;
      });
      baseTotal -= discamt;
    }

    if (isExchangeOrder) {
      let discamt = 0;
      (products || []).forEach((product) => {
        discamt +=
          product.discaftertax == 1
            ? Number(product.discountval) * Number(product.exchangeQuantity) ||
              0
            : 0;
      });
      baseTotal -= discamt;

      let totalGlobalDiscountShare = 0;
      for (const product of products || []) {
        const exchangeQty = Number(product.exchangeQuantity) || 0;
        if (product?.returned && exchangeQty > 0) {
          totalGlobalDiscountShare += this.getProratedGlobalDiscountShare(
            product,
            exchangeQty,
          );
        }
      }
      return Math.max(0, baseTotal - totalGlobalDiscountShare);
    }

    return Math.max(0, baseTotal - overAllDiscountAmount);
  }

  /**
   * Overall-discount share for `qty` units of an invoice line being returned.
   * globalDiscountShare was split over the qty originally sold (soldQuantity);
   * increaseQuantity is only the qty still returnable, so it is the fallback.
   */
  getProratedGlobalDiscountShare(product: Product, qty: number): number {
    const share = Number(product?.globalDiscountShare) || 0;
    if (share <= 0 || !qty) return 0;
    const soldQty =
      Number(product.soldQuantity) || Number(product.increaseQuantity) || 1;
    return (share / soldQty) * qty;
  }

  /**
   * Subtotal after line item discounts
   */
  getSubtotalAfterLineDiscount(products: Product[]): number {
    let subtotal = 0;
    for (const product of products || []) {
      if (product?.increaseQuantity && !product?.returned) {
        const priceAfterLineDiscount = product.sellingprice || 0;
        subtotal += priceAfterLineDiscount * product.increaseQuantity;
      }
    }
    return subtotal;
  }

  /**
   * Total value of free-qty items given to the customer.
   * Formula: sellingprice (excl. tax & discount) × cart_free_qty
   * No tax or discount is applied — this is a pure base-price × qty sum.
   */
  getTotalFreeAmount(products: Product[]): number {
    let total = 0;
    for (const product of products || []) {
      if (!product?.returned) {
        const cartFreeQty = Number(product.cart_free_qty) || 0;
        if (cartFreeQty > 0) {
          // sellingprice is the base price excl. tax stored on the product
          const basePrice = product.sellingprice || 0;
          total += basePrice * cartFreeQty;
        }
      }
    }
    return total;
  }

  /**
   * Overall discount amount
   */
  getOverAllDiscountAmount(
    mode: 'percentage' | 'amount',
    percent: number,
    amount: number,
    subtotalAfterLineDiscount: number,
  ): number {
    if (mode === 'percentage') {
      return (
        Math.round(((subtotalAfterLineDiscount * percent) / 100) * 100) / 100
      );
    }
    return amount || 0;
  }

  /**
   * Handle overall discount value change
   */
  onOverAllDiscountChange(
    value: number,
    mode: 'percentage' | 'amount',
    subtotalAfterLineDiscount: number,
  ): { overAllDiscount: number; overAllDiscountPercent: number } {
    let overAllDiscount = 0;
    let overAllDiscountPercent = 0;

    if (mode === 'percentage') {
      overAllDiscountPercent = Math.min(Math.max(value || 0, 0), 100);
      overAllDiscount =
        Math.round(
          ((subtotalAfterLineDiscount * overAllDiscountPercent) / 100) * 100,
        ) / 100;
    } else {
      const maxDiscount = subtotalAfterLineDiscount;
      overAllDiscount = Math.min(Math.max(value || 0, 0), maxDiscount);
      overAllDiscountPercent =
        subtotalAfterLineDiscount > 0
          ? Math.round((overAllDiscount / subtotalAfterLineDiscount) * 100 * 100) /
            100
          : 0;
    }

    return { overAllDiscount, overAllDiscountPercent };
  }

  /**
   * Calculate and assign globalDiscountShare on each product
   */
  calculateGlobalDiscountShares(
    products: Product[],
    subtotalAfterLineDiscount: number,
    overAllDiscountAmount: number,
  ): void {
    if (subtotalAfterLineDiscount <= 0 || overAllDiscountAmount <= 0) {
      (products || []).forEach((product) => {
        (product as any).globalDiscountShare = 0;
      });
      return;
    }

    let totalShareAssigned = 0;
    const eligibleProducts = (products || []).filter(
      (p) => p?.increaseQuantity && !p?.returned,
    );

    eligibleProducts.forEach((product, index) => {
      const priceAfterLineDiscount =
        (product.sellingprice || 0) * (product.increaseQuantity || 1);
      const weight = priceAfterLineDiscount / subtotalAfterLineDiscount;

      if (index === eligibleProducts.length - 1) {
        (product as any).globalDiscountShare =
          Math.round((overAllDiscountAmount - totalShareAssigned) * 100) / 100;
      } else {
        const share = Math.round(overAllDiscountAmount * weight * 100) / 100;
        (product as any).globalDiscountShare = share;
        totalShareAssigned += share;
      }
    });
  }

  /**
   * Get round off amount
   */
  getRoundOffAmount(exactTotal: number, roundedTotal: number): number {
    return Number((roundedTotal - exactTotal).toFixed(2));
  }

  /**
   * Get rounded total
   */
  getRoundedTotal(exactTotal: number): number {
    const formattedExactTotal = exactTotal.toFixed(2);
    return Math.round(Number(formattedExactTotal));
  }

  /**
   * Validate if quotation can be completed
   */
  canCompleteQuotation(formData: any, products: Product[]): boolean {
    const isMobileMandatory =
      this.clientSettingsService.isCustomerMobileMandatory();

    const hasRequiredFields = isMobileMandatory
      ? !!(formData?.mobileNumber && formData.mobileNumber.trim() !== '')
      : true;

    const hasProducts = !!(products && products.length > 0);
    return hasRequiredFields && hasProducts;
  }

  /**
   * Check if any mobile variant selection is pending
   */
  hasPendingVariantSelection(products: Product[]): boolean {
    if (!this.industryService.isMobile()) return false;

    return (products || []).some(
      (product) =>
        product.productmapvalues &&
        product.productmapvalues.length > 0 &&
        !product.selectedMapValue,
    );
  }
}
